import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Plus, Home, MapPin, Phone, Users } from 'lucide-react';
import { toast } from 'sonner';

const HousesPage = () => {
  const { api } = useAuth();
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  const [newHouse, setNewHouse] = useState({
    name: '',
    address: '',
    phone: '',
    staffing_ratio: '1:4',
    capacity: 4
  });

  useEffect(() => {
    fetchHouses();
  }, []);

  const fetchHouses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/houses');
      setHouses(res.data);
    } catch (error) {
      toast.error('Failed to load houses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post('/houses', newHouse);
      toast.success('House created successfully');
      setCreateDialogOpen(false);
      setNewHouse({
        name: '',
        address: '',
        phone: '',
        staffing_ratio: '1:4',
        capacity: 4
      });
      fetchHouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create house');
    }
  };

  const columns = [
    {
      header: 'Name',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Home className="h-5 w-5" />
          </div>
          <span className="font-medium">{row.name}</span>
        </div>
      )
    },
    {
      header: 'Address',
      cell: (row) => (
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="h-4 w-4" />
          {row.address}
        </div>
      )
    },
    {
      header: 'Phone',
      cell: (row) => (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Phone className="h-4 w-4" />
          {row.phone || '—'}
        </div>
      )
    },
    {
      header: 'Staffing Ratio',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          {row.staffing_ratio}
        </div>
      )
    },
    {
      header: 'Capacity',
      accessor: 'capacity'
    },
    {
      header: 'Status',
      cell: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.is_active 
            ? 'bg-green-500/10 text-green-500' 
            : 'bg-red-500/10 text-red-500'
        }`}>
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="houses-page">
      <PageHeader 
        title="Houses"
        description="Manage SIL and community support houses"
        actions={
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-house-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add House
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New House</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>House Name *</Label>
                  <Input 
                    value={newHouse.name}
                    onChange={(e) => setNewHouse({...newHouse, name: e.target.value})}
                    placeholder="e.g., Sunrise House"
                    data-testid="house-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Address *</Label>
                  <Input 
                    value={newHouse.address}
                    onChange={(e) => setNewHouse({...newHouse, address: e.target.value})}
                    placeholder="Full address"
                    data-testid="house-address"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input 
                    value={newHouse.phone}
                    onChange={(e) => setNewHouse({...newHouse, phone: e.target.value})}
                    placeholder="Contact number"
                    data-testid="house-phone"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Staffing Ratio</Label>
                    <Select 
                      value={newHouse.staffing_ratio} 
                      onValueChange={(v) => setNewHouse({...newHouse, staffing_ratio: v})}
                    >
                      <SelectTrigger data-testid="house-ratio">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1:1">1:1</SelectItem>
                        <SelectItem value="1:2">1:2</SelectItem>
                        <SelectItem value="1:3">1:3</SelectItem>
                        <SelectItem value="1:4">1:4</SelectItem>
                        <SelectItem value="2:4">2:4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity</Label>
                    <Input 
                      type="number"
                      value={newHouse.capacity}
                      onChange={(e) => setNewHouse({...newHouse, capacity: parseInt(e.target.value)})}
                      min={1}
                      max={10}
                      data-testid="house-capacity"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="save-house-btn">Create House</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <DataTable 
        columns={columns}
        data={houses}
        emptyMessage="No houses found"
      />
    </div>
  );
};

export default HousesPage;
